import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTopOpportunities, type TokenOpportunity } from "@/hooks/useTopOpportunities";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowUpDown, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { ScoreHelpLink } from "@/components/ScoreHelpLink";

type SortKey = "score" | "change24h" | "change7d" | "cap" | "volume";

const fmtPrice = (p: number) =>
  p >= 1 ? `A$${p.toLocaleString("en-AU", { maximumFractionDigits: 2 })}` : `A$${p.toFixed(p < 0.01 ? 6 : 4)}`;

const fmtBig = (v: number) =>
  v >= 1e9 ? `A$${(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `A$${(v / 1e6).toFixed(0)}M` : `A$${(v / 1e3).toFixed(0)}K`;

const EX_LABEL: Record<string, string> = { swyftx: "SWX", coinspot: "CSP", btcmarkets: "BTCM", independentreserve: "IR" };

function useExchangeAvailability() {
  return useQuery({
    queryKey: ["exchange-availability-map"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exchange_symbol_map")
        .select("exchange, exchange_symbol")
        .limit(2000);
      if (error) throw error;
      const map = new Map<string, string[]>();
      (data ?? []).forEach((r: any) => {
        const sym = String(r.exchange_symbol || "").toUpperCase();
        const list = map.get(sym) ?? [];
        const label = EX_LABEL[String(r.exchange).toLowerCase()] ?? String(r.exchange).slice(0, 4).toUpperCase();
        if (!list.includes(label)) list.push(label);
        map.set(sym, list);
      });
      return map;
    },
    staleTime: 30 * 60 * 1000,
  });
}

function Pct({ value }: { value: number | null }) {
  if (value === null || value === undefined || !Number.isFinite(value))
    return <span className="text-muted-foreground">—</span>;
  return (
    <span className={cn("font-mono tabular-nums", value >= 0 ? "text-success" : "text-destructive")}>
      {value >= 0 ? "+" : ""}
      {value.toFixed(1)}%
    </span>
  );
}

/**
 * Dense, sortable TOSS movers table — replaces the card-grid mosaic so 20+
 * tokens are scannable at a glance.
 */
export function TossMoversTable() {
  const { data, isLoading } = useTopOpportunities({ limit: 25 });
  const { data: exMap } = useExchangeAvailability();
  const [sort, setSort] = useState<SortKey>("score");
  const [dir, setDir] = useState<"desc" | "asc">("desc");

  const rows = useMemo(() => {
    const val = (o: TokenOpportunity, k: SortKey) =>
      k === "score"
        ? o.opportunity_score
        : k === "change24h"
          ? (o.price_change_24h ?? -999)
          : k === "change7d"
            ? (o.price_change_7d ?? -999)
            : k === "cap"
              ? o.market_cap
              : o.volume_24h;
    return [...(data ?? [])].sort((a, b) => (dir === "desc" ? val(b, sort) - val(a, sort) : val(a, sort) - val(b, sort)));
  }, [data, sort, dir]);

  const toggle = (k: SortKey) => {
    if (k === sort) setDir((d) => (d === "desc" ? "asc" : "desc"));
    else {
      setSort(k);
      setDir("desc");
    }
  };

  const Th = ({ k, children, className }: { k: SortKey; children: React.ReactNode; className?: string }) => (
    <th className={cn("px-2 py-2 text-right font-semibold", className)}>
      <button
        onClick={() => toggle(k)}
        className={cn(
          "inline-flex items-center gap-1 hover:text-foreground transition-colors",
          sort === k ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {children}
        <ArrowUpDown className="w-3 h-3" />
      </button>
    </th>
  );

  return (
    <section aria-label="TOSS movers" className="container mx-auto px-4 py-4">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-bold uppercase tracking-[0.15em] flex items-center gap-2">
          TOSS movers <ScoreHelpLink />
        </h2>
        <Link to="/opportunities" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
          All opportunities <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {isLoading ? (
        <Skeleton className="h-72" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No scored tokens available right now.</p>
      ) : (
        <div className="overflow-x-auto border border-border/60 rounded-md">
          <table className="w-full text-xs">
            <thead className="bg-secondary/50 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-2 py-2 text-left font-semibold text-muted-foreground">Token</th>
                <th className="px-2 py-2 text-right font-semibold text-muted-foreground">Price</th>
                <Th k="score" className="text-muted-foreground">Score</Th>
                <Th k="change24h" className="text-muted-foreground">24h</Th>
                <Th k="change7d" className="text-muted-foreground">7d</Th>
                <Th k="cap" className="text-muted-foreground">Mkt cap</Th>
                <Th k="volume" className="text-muted-foreground">Vol 24h</Th>
                <th className="px-2 py-2 text-left font-semibold text-muted-foreground">AU listing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {rows.map((o) => {
                const sym = o.token_symbol.toUpperCase();
                const exchanges = exMap?.get(sym) ?? [];
                return (
                  <tr key={o.id} className="hover:bg-secondary/40 transition-colors">
                    <td className="px-2 py-1.5">
                      <Link to={`/coin-reviewer?symbol=${encodeURIComponent(sym)}`} className="hover:underline">
                        <span className="font-bold">{sym}</span>
                        <span className="ml-2 text-muted-foreground hidden md:inline">{o.token_name}</span>
                      </Link>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtPrice(o.current_price)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold tabular-nums">
                      {Math.round(o.opportunity_score)}
                    </td>
                    <td className="px-2 py-1.5 text-right"><Pct value={o.price_change_24h} /></td>
                    <td className="px-2 py-1.5 text-right"><Pct value={o.price_change_7d} /></td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-muted-foreground">
                      {fmtBig(o.market_cap)}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-muted-foreground">
                      {fmtBig(o.volume_24h)}
                    </td>
                    <td className="px-2 py-1.5 text-[10px] font-mono text-muted-foreground">
                      {exchanges.length ? exchanges.join(" · ") : "AU-listed"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">
        Scored universe is limited to tokens listed on Australian exchanges (Swyftx / CoinSpot). Named exchange codes
        show where a verified symbol mapping exists.
      </p>
    </section>
  );
}
